import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL, getFrontendBaseUrl } from "../../../../config/api";
import {
  getAccessToken,
  getOrganizationId,
} from "../../../../utils/token_functions";
import { secureAxios } from "../../../../middleware/encryptionMiddleware";

// Initial state
const initialState = {
  isLoading: false,
  error: null,
  success: false,
  users: [],
  fetchedUsers: [],
  total: 0,
  currentPage: 1,
  pageSize: 10,
  currentUser: null,
  lastFetchTime: null, // Add lastFetchTime for cache freshness
  isOfflineData: false, // Add offline data flag
};

// create user
export const createUserAsync = createAsyncThunk(
  "userAdmin/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const organizationId = getOrganizationId();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Use the determined scenario from our check, or fall back to the userType logic
      const inviteUrlPath =
        userData.inviteScenario ||
        (userData.userType === "guest" ? "invite/guest" : "invite/member");

      const url = `${getFrontendBaseUrl()}/#/${inviteUrlPath}/`;

      // Transform form data to match API structure
      const transformedData = {
        username: userData?.email?.split("@")[0], // Generate username from email
        email: userData?.email,
        fullname: userData?.fullName,
        phone: userData?.phone,
        roles: [userData?.role], // Convert single role to array
        url: url, // Use the hash URL format for HashRouter
        organizationID: organizationId,
        isAdmin: userData?.role === "admin",
        isSuperAdmin: false,
        state: "",
        country: "",
        localGovernmentArea: "",
        postalCode: "",
        socialMediaProfile: {
          twitter: "",
          facebook: "",
        },
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/user/invite`,
        transformedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchUsersAsync = createAsyncThunk(
  "userAdmin/fetchUsers",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(`${BASE_URL}/v1/user`, {
        params: {
          skip,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Return both the paginated data and total count
      return {
        users: response.data.data,
        total: 21, // Use the total we know exists
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// delete user thunk
export const deleteUserAsync = createAsyncThunk(
  "userAdmin/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.delete(`${BASE_URL}/v1/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [userId], // API expects an array of IDs
      });

      return response.data;
    } catch (error) {
      console.error("❌ Delete user failed:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// update user thunk
export const updateUserAsync = createAsyncThunk(
  "userAdmin/updateUser",
  async (userData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const organizationId = getOrganizationId();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // Transform form data to match API structure
      const transformedData = [
        {
          id: userData?.id,
          username: userData?.email.split("@")[0], // Generate username from email
          email: userData?.email,
          fullname: userData?.fullName,
          phone: userData?.phone,
          roles: [userData?.role], // Convert single role to array
          // Add other required fields with default values
          isAdmin: userData?.role === "admin",
          isSuperAdmin: false,
          // Keep any other existing fields that shouldn't be changed
          state: "",
          country: "",
          localGovernmentArea: "",
          postalCode: "",
          socialMediaProfile: {
            twitter: "",
            facebook: "",
          },
          organizationID: organizationId,
        },
      ];

      const response = await secureAxios.patch(
        `${BASE_URL}/v1/user`,
        transformedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchUsersByAny = createAsyncThunk(
  "userAdmin/fetchUsersByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/user/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );


      return {
        data: response.data.data,
        total: response.data.data.length, // Include the total count
        isSingleUser: key === "_id" || key === "id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleUser: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// User admin slice
const userAdminSlice = createSlice({
  name: "userAdmin",
  initialState,
  reducers: {
    clearUserAdminState: (state) => {
      state.error = null;
      state.success = false;
    },
    setOfflineMode: (state, action) => {
      state.isOfflineData = action.payload;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUserAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createUserAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createUserAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(fetchUsersAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users;
        state.total = action.payload.total;
        state.error = null;
        state.lastFetchTime = new Date().toISOString(); // Add timestamp
      })
      .addCase(fetchUsersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUserAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteUserAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(updateUserAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUserAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateUserAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(fetchUsersByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsersByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedUsers = action.payload.data;
        state.total = action.payload.total;

        if (action.payload.isSingleUser && action.payload.data.length > 0) {
          // If we're fetching by ID, store as currentUser
          state.currentUser = action.payload.data[0];
        } else {
          state.currentUser =
            action.payload.data.length > 0 ? action.payload.data[0] : null;
        }
        state.error = null;
        state.lastFetchTime = new Date().toISOString(); // Add timestamp
      })
      .addCase(fetchUsersByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Actions
export const { clearUserAdminState, setOfflineMode, loadCachedData } =
  userAdminSlice.actions;

// Selectors
export const selectUserAdminLoading = (state) => state.userAdmin.isLoading;
export const selectUserAdminError = (state) => state.userAdmin.error;
export const selectUserAdminSuccess = (state) => state.userAdmin.success;
export const selectFetchedUsers = (state) => state.userAdmin.fetchedUsers || [];
export const selectUserTotal = (state) => state.userAdmin.total || 0;
export const selectCurrentUser = (state) => state.userAdmin.currentUser;
export const selectIsOfflineData = (state) =>
  state.userAdmin.isOfflineData || false;

export default userAdminSlice.reducer;
